import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  selectTool,
  currentTool,
  subscribeToolChange,
  subscribeChartWidget,
  isChartReady,
  removeAllDrawings,
  getToggle,
  toggle,
} from "./chartToolBridge";
import ICONS from "./chartToolIcons";

/** The library ships its icons as SVG markup strings; they are static assets
 *  from the bundle, not user input. */
function Icon({ svg }) {
  return (
    <span
      className="fx-chart-rail__icon"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

// Mirrors the native left toolbar: one entry per tool group in the same order
// (cursors, trend lines, Gann/Fibonacci, patterns, forecasting, shapes,
// annotation, font icons) followed by the measure and zoom buttons.
const TOOLS = [
  { tool: "cursor", title: "Cross", svg: ICONS.cursor },
  { tool: "dot", title: "Dot", svg: ICONS.dot },
  { tool: "arrow_cursor", title: "Arrow", svg: ICONS.arrow },
  { tool: "eraser", title: "Eraser", svg: ICONS.eraser },
  { tool: "trend_line", title: "Trend line", svg: ICONS.trendLine },
  { tool: "horizontal_line", title: "Horizontal line", svg: ICONS.horzLine },
  { tool: "fib_retracement", title: "Fib retracement", svg: ICONS.fibRetracement },
  { tool: "xabcd_pattern", title: "XABCD pattern", svg: ICONS.pattern },
  { tool: "long_position", title: "Long position", svg: ICONS.longPosition },
  { tool: "brush", title: "Brush", svg: ICONS.brush },
  { tool: "rectangle", title: "Rectangle", svg: ICONS.rectangle },
  { tool: "text", title: "Text", svg: ICONS.text },
  { tool: "measure", title: "Measure", svg: ICONS.measure },
  { tool: "zoom", title: "Zoom in", svg: ICONS.zoom },
];

// selectLineTool("emoji") always drops the one default glyph, so the emoji the
// user wants has to be passed in options — hence this picker.
const EMOJIS = [
  "😀", "😃", "😄", "😁", "😆", "😅", "😂", "🙂",
  "😉", "😍", "😎", "🤓", "🤨", "😐", "😏", "😒",
  "😔", "😢", "😡", "🤯", "😱", "🥳", "🤔", "🤝",
  "👍", "👎", "👌", "👏", "🙏", "💪", "👀", "🔥",
  "💰", "💸", "💎", "📈", "📉", "📊", "🚀", "🐂",
  "🐻", "⚠️", "❗", "❓", "✅", "❌", "⭐", "🎯",
];

export default function ChartToolRail() {
  const [active, setActive] = useState("cursor");
  const [magnet, setMagnet] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [locked, setLocked] = useState(false);
  const [emojiAt, setEmojiAt] = useState(null);
  const emojiBtnRef = useRef(null);
  const emojiPopRef = useRef(null);

  useEffect(() => {
    let unsubTool = () => {};
    let poll = null;

    const attach = () => {
      if (!isChartReady()) return false;
      setActive(currentTool() || "cursor");
      setMagnet(getToggle("magnet"));
      setHidden(getToggle("hide"));
      setLocked(getToggle("lock"));
      unsubTool();
      unsubTool = subscribeToolChange(() => setActive(currentTool() || "cursor"));
      return true;
    };

    const unsubWidget = subscribeChartWidget((w) => {
      unsubTool();
      unsubTool = () => {};
      if (!w) return;
      if (!attach() && !poll) {
        poll = setInterval(() => {
          if (attach()) {
            clearInterval(poll);
            poll = null;
          }
        }, 300);
      }
    });

    return () => {
      if (poll) clearInterval(poll);
      unsubTool();
      unsubWidget();
    };
  }, []);

  // The rail scrolls, so the picker is positioned in viewport coordinates to
  // keep it out of the overflow clip.
  useEffect(() => {
    if (!emojiAt) return undefined;
    const onDown = (e) => {
      if (emojiPopRef.current?.contains(e.target)) return;
      if (emojiBtnRef.current?.contains(e.target)) return;
      setEmojiAt(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setEmojiAt(null);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [emojiAt]);

  const pick = useCallback(async (tool) => {
    setActive(tool);
    setEmojiAt(null);
    await selectTool(tool);
  }, []);

  const openEmoji = useCallback(() => {
    setEmojiAt((prev) => {
      if (prev) return null;
      const r = emojiBtnRef.current?.getBoundingClientRect();
      if (!r) return null;
      const height = 250;
      return {
        left: Math.round(r.right + 4),
        top: Math.round(Math.min(r.top, Math.max(8, window.innerHeight - height - 8))),
      };
    });
  }, []);

  const pickEmoji = useCallback(async (emoji) => {
    setActive("emoji");
    setEmojiAt(null);
    await selectTool("emoji", { emoji });
  }, []);

  return (
    <div className="fx-chart-rail" role="toolbar" aria-label="Chart drawing tools">
      <div className="fx-chart-rail__group">
        {TOOLS.map(({ tool, title, svg }) => (
          <button
            key={tool}
            type="button"
            className={`fx-chart-rail__btn${active === tool ? " is-active" : ""}`}
            title={title}
            aria-label={title}
            aria-pressed={active === tool}
            onClick={() => pick(tool)}
          >
            <Icon svg={svg} />
          </button>
        ))}

        <button
          ref={emojiBtnRef}
          type="button"
          className={`fx-chart-rail__btn${active === "emoji" ? " is-active" : ""}${emojiAt ? " is-open" : ""}`}
          title="Emoji"
          aria-label="Emoji"
          aria-haspopup="true"
          aria-expanded={!!emojiAt}
          onClick={openEmoji}
        >
          <Icon svg={ICONS.emoji} />
        </button>
      </div>

      <div className="fx-chart-rail__group fx-chart-rail__group--end">
        <button
          type="button"
          className={`fx-chart-rail__btn${magnet ? " is-active" : ""}`}
          title="Magnet mode"
          aria-label="Magnet mode"
          aria-pressed={magnet}
          onClick={() => setMagnet(toggle("magnet"))}
        >
          <Icon svg={magnet ? ICONS.strongMagnet : ICONS.magnet} />
        </button>
        <button
          type="button"
          className={`fx-chart-rail__btn${locked ? " is-active" : ""}`}
          title={locked ? "Unlock all drawings" : "Lock all drawings"}
          aria-label="Lock all drawings"
          aria-pressed={locked}
          onClick={() => setLocked(toggle("lock"))}
        >
          <Icon svg={locked ? ICONS.lockActive : ICONS.lock} />
        </button>
        <button
          type="button"
          className={`fx-chart-rail__btn${hidden ? " is-active" : ""}`}
          title={hidden ? "Show all drawings" : "Hide all drawings"}
          aria-label="Hide all drawings"
          aria-pressed={hidden}
          onClick={() => setHidden(toggle("hide"))}
        >
          <Icon svg={hidden ? ICONS.hideActive : ICONS.hide} />
        </button>
        <button
          type="button"
          className="fx-chart-rail__btn fx-chart-rail__btn--danger"
          title="Remove all drawings"
          aria-label="Remove all drawings"
          onClick={removeAllDrawings}
        >
          <Icon svg={ICONS.removeAll} />
        </button>
      </div>

      {emojiAt && (
        <div
          ref={emojiPopRef}
          className="fx-chart-rail__emoji-pop"
          style={{ left: emojiAt.left, top: emojiAt.top }}
          role="menu"
          aria-label="Pick an emoji"
        >
          {EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="fx-chart-rail__emoji"
              title={`Place ${emoji}`}
              onClick={() => pickEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
